import { useState, useLayoutEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDispatch, useSelector } from '_common/hooks/redux';
import { closeAndResetModal } from '_common/modals/ModalsSlice';
import { useEditProfileMutation } from 'Settings/pages/UserSettingsPage/UserSettingsApi';
import { useGetCurrentUserQuery } from '_common/services/api/authority';

import { Input, Modal, InputField, Button } from '@ets/design-system';

const MODAL = 'ChangeNameModal';

const ChangeNameModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const disabledFields = useSelector((state) => state.modals[MODAL].disabledFields);
  const { data: currentUser } = useGetCurrentUserQuery();
  const [editProfile] = useEditProfileMutation();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (isOpen && currentUser) {
      setFirstName(currentUser.profile.first_name);
      setLastName(currentUser.profile.last_name);

      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen, currentUser, currentUser?.profile.first_name, currentUser?.profile.last_name]);

  const handleChangeName = () => {
    if (currentUser) {
      editProfile({
        first_name: firstName,
        last_name: lastName,
        userId: currentUser.profile.id,
      });
      close();
    }
  };

  const close = () => {
    dispatch(closeAndResetModal(MODAL));
  };

  return (
    <Modal width="75rem" open={!!isOpen} close={close} testId="change-name">
      <Modal.Header>
        <FormattedMessage id="settings.name.header" />
      </Modal.Header>
      <Modal.Body>
        <InputField
          label={intl.formatMessage({ id: 'settings.name.firstName' })}
          size="l"
          disabled={disabledFields.firstName}
          testId="change-name-modal-first-name"
        >
          <Input
            size="l"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            ref={inputRef}
            placeholder={intl.formatMessage({ id: 'FIRST_NAME_PLACEHOLDER' })}
            testId="change-name-modal-first-name"
            disabled={disabledFields.firstName}
          />
        </InputField>
        <InputField
          size="l"
          label={intl.formatMessage({ id: 'settings.name.lastName' })}
          disabled={disabledFields.lastName}
          testId="change-name-modal-last-name"
        >
          <Input
            size="l"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={intl.formatMessage({ id: 'LAST_NAME_PLACEHOLDER' })}
            testId="change-name-modal-last-name"
            disabled={disabledFields.lastName}
          />
        </InputField>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="change-name-modal-close">
          <FormattedMessage id="global.close" />
        </Button>
        <Button
          size="m"
          variant="primary"
          onClick={handleChangeName}
          testId="change-name-modal-save"
          style={{ margin: '0 0 0 1rem' }}
          disabled={disabledFields.lastName && disabledFields.firstName}
        >
          <FormattedMessage id="global.save" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeNameModal;
